import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    BriefcaseIcon,
    UserGroupIcon,
    ChartBarIcon,
    ArrowTrendingUpIcon,
    CurrencyDollarIcon,
    ClipboardDocumentListIcon,
    CalendarDaysIcon,
    MicrophoneIcon,
    PaperAirplaneIcon,
    StarIcon,
    CakeIcon,
    MegaphoneIcon,
    ChatBubbleLeftRightIcon,
    RocketLaunchIcon,
    TrophyIcon,
    DocumentTextIcon,
    XMarkIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

// --- MOCK DATA ---
const mockDashboardData = {
    userName: "Alex",
    totalEmployees: 1300,
    openPositions: 45,
    attritionRate: 7.31,
    costPerHire: 1400,
    recruitmentFunnel: {
        applied: 400,
        screened: 250,
        interviewed: 80,
        offered: 15,
    },
    employeesByDept: {
        Technology: 450,
        Sales: 320,
        Operations: 280,
        HR: 150,
        Marketing: 100,
    },
    recentHires: [
        { id: 1, name: 'Jane Doe', role: 'Software Engineer', dept: 'Technology', date: 'Sept 20' },
        { id: 2, name: 'John Smith', role: 'Data Analyst', dept: 'Technology', date: 'Sept 18' },
        { id: 3, name: 'Emily White', role: 'HR Generalist', dept: 'Human Resources', date: 'Sept 15' },
        { id: 4, name: 'David Lee', role: 'Marketing Specialist', dept: 'Marketing', date: 'Sept 12' },
        { id: 5, name: 'Sarah Connor', role: 'Project Manager', dept: 'Operations', date: 'Sept 10' },
        { id: 6, name: 'Michael Chen', role: 'UX Designer', dept: 'Technology', date: 'Sept 5' },
    ],
    employeeMilestones: [
        { id: 1, name: 'Jessica Alba', event: 'Birthday', date: 'Oct 15' },
        { id: 2, name: 'Tom Hanks', event: 'Work Anniversary (5 yrs)', date: 'Oct 20' },
        { id: 3, name: 'Laura Miller', event: 'Birthday', date: 'Oct 22' },
        { id: 4, name: 'Brad Pitt', event: 'Work Anniversary (2 yrs)', date: 'Oct 25' },
        { id: 5, name: 'Angelina Jolie', event: 'Birthday', date: 'Oct 28' },
        { id: 6, name: 'Leonardo DiCaprio', event: 'Work Anniversary (10 yrs)', date: 'Nov 1' },
    ],
    announcements: [
        { id: 1, title: 'Company-wide meeting next Friday', date: 'Oct 14' },
        { id: 2, title: 'New policy on remote work', date: 'Oct 10' },
        { id: 3, title: 'Holiday party planning committee', date: 'Oct 8' },
        { id: 4, title: 'Q3 financial report is live', date: 'Oct 1' },
    ],
    leaveRequests: [
        { id: 1, name: 'Anjali Sharma', reason: 'Personal Leave', duration: 'Oct 25 - Oct 28', status: 'Pending' },
        { id: 2, name: 'Raj Kumar', reason: 'Sick Leave', duration: 'Oct 26', status: 'Pending' },
        { id: 3, name: 'Priya Singh', reason: 'Vacation', duration: 'Nov 1 - Nov 5', status: 'Pending' },
    ],
};

const barChartOptions = {
    responsive: true,
    indexAxis: 'y',
    plugins: {
        legend: { display: false },
        title: {
            display: true,
            text: 'Recruitment Funnel',
            font: { size: 14, weight: 'bold' },
            color: '#374151'
        },
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: { display: false }
        },
        y: {
            grid: { display: false }
        }
    }
};

const barChartData = {
    labels: ['Applied', 'Screened', 'Interviewed', 'Offered'],
    datasets: [{
        label: 'Number of Candidates',
        data: [
            mockDashboardData.recruitmentFunnel.applied,
            mockDashboardData.recruitmentFunnel.screened,
            mockDashboardData.recruitmentFunnel.interviewed,
            mockDashboardData.recruitmentFunnel.offered
        ],
        backgroundColor: ['#f97316', '#eab308', '#22c55e', '#3b82f6'],
        borderRadius: 8,
    }],
};

const pieChartOptions = {
    responsive: true,
    plugins: {
        legend: {
            position: 'right',
            labels: {
                boxWidth: 10,
                padding: 10
            }
        },
        title: {
            display: true,
            text: 'Employees by Department',
            font: { size: 14, weight: 'bold' },
            color: '#374151'
        },
        tooltip: {
            callbacks: {
                label: (context) => {
                    const label = context.label || '';
                    const value = context.parsed;
                    const total = Object.values(mockDashboardData.employeesByDept).reduce((a, b) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1) + '%';
                    return `${label}: ${value} (${percentage})`;
                }
            }
        }
    },
};

const pieChartData = {
    labels: Object.keys(mockDashboardData.employeesByDept),
    datasets: [{
        data: Object.values(mockDashboardData.employeesByDept),
        backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6'],
        borderWidth: 1,
        borderColor: '#ffffff',
    }],
};

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 20,
            duration: 0.6
        }
    }
};

const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const DashboardCard = ({ title, value, icon, bgColor, textColor, trend }) => (
    <motion.div
        variants={cardVariants}
        className={`bg-white p-4 rounded-xl shadow-md flex flex-col items-start space-y-1 transform hover:scale-105 transition-transform duration-300 ease-in-out cursor-pointer`}
    >
        <div className={`p-2 rounded-full ${bgColor}`}>
            {React.cloneElement(icon, { className: `h-6 w-6 ${textColor}` })}
        </div>
        <h3 className="text-2xl font-extrabold text-gray-900">{value}</h3>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        {trend && (
            <div className={`flex items-center text-xs font-semibold ${trend === 'up' ? 'text-red-500' : 'text-green-500'}`}>
                <ArrowTrendingUpIcon className={`h-4 w-4 ${trend === 'up' ? 'rotate-0' : 'rotate-180'}`} />
                {mockDashboardData.attritionRate}%
            </div>
        )}
    </motion.div>
);

const QuickLinkCard = ({ title, icon: Icon, path }) => (
    <motion.div
        whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0,0,0,0.1)" }}
        whileTap={{ scale: 0.95 }}
        className="bg-white p-4 rounded-xl shadow-md flex flex-col items-center justify-center text-center transform transition-transform duration-300"
    >
        <Link to={path} className="flex flex-col items-center space-y-2">
            <div className="p-3 rounded-full bg-indigo-50 text-indigo-600">
                <Icon className="h-6 w-6" />
            </div>
            <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        </Link>
    </motion.div>
);

const AIChatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (input.trim()) {
            const newMessages = [...messages, { text: input, from: 'user' }];
            setMessages(newMessages);
            setInput('');
            setTimeout(() => {
                setMessages([...newMessages, { text: `Hi there! You asked about: "${input}". What else can I help with?`, from: 'ai' }]);
            }, 500);
        }
    };

    const handleVoiceCommand = () => {
        alert("Voice command activated. Please speak now.");
        setMessages([...messages, { text: "Voice command received. Awaiting input...", from: 'ai' }]);
    };

    return (
        <div className="fixed bottom-4 right-4 z-50">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 50 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-xl shadow-2xl w-72 md:w-80 overflow-hidden flex flex-col h-96"
                    >
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 flex justify-between items-center">
                            <h4 className="text-md font-bold">AI Bot</h4>
                            <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-200">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 p-3 overflow-y-auto space-y-3 text-sm">
                            {messages.map((msg, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: msg.from === 'user' ? 20 : -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: msg.from === 'user' ? 20 : -20 }}
                                    transition={{ duration: 0.2 }}
                                    className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`p-2 rounded-lg max-w-[85%] ${msg.from === 'user' ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                                        {msg.text}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-200 flex space-x-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                className="flex-1 px-3 py-1 border rounded-full focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                                placeholder="Type a message..."
                            />
                            <button
                                type="button"
                                onClick={handleVoiceCommand}
                                className="p-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors"
                            >
                                <MicrophoneIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="submit"
                                className="p-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors"
                            >
                                <PaperAirplaneIcon className="w-4 h-4" />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="bg-indigo-600 text-white p-3 rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
            >
                <ChatBubbleLeftRightIcon className="w-6 h-6" />
            </button>
        </div>
    );
};

const LeaveRequests = () => {
    const [requests, setRequests] = useState(mockDashboardData.leaveRequests);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const intervalRef = useRef(null);

    const startInterval = () => {
        intervalRef.current = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % requests.length);
        }, 5000);
    };

    const stopInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    useEffect(() => {
        if (!isHovered) {
            startInterval();
        } else {
            stopInterval();
        }
        return () => stopInterval();
    }, [requests, isHovered]);

    const handleAction = (id, action) => {
        const updatedRequests = requests.filter(req => req.id !== id);
        setRequests(updatedRequests);
        setCurrentIndex(0);
        alert(`Leave request for ${requests.find(r => r.id === id).name} has been ${action}.`);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="bg-white p-4 rounded-xl shadow-md w-full relative h-48 sm:h-auto overflow-hidden"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <h3 className="text-md font-bold text-gray-900 mb-3">Leave Requests & Approvals 📝</h3>
            <AnimatePresence mode="wait">
                {requests.length > 0 ? (
                    <motion.div
                        key={requests[currentIndex].id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className="flex flex-col h-full"
                    >
                        <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2 justify-between">
                                <div className="flex items-center space-x-2">
                                    <ClockIcon className="h-5 w-5 text-yellow-500" />
                                    <h4 className="text-sm font-semibold text-gray-900">{requests[currentIndex].name}</h4>
                                </div>
                                <div className="flex space-x-2">
                                    <motion.button
                                        onClick={() => handleAction(requests[currentIndex].id, 'approved')}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="text-green-500 hover:text-green-600 transition-colors"
                                    >
                                        <CheckCircleIcon className="h-6 w-6" />
                                    </motion.button>
                                    <motion.button
                                        onClick={() => handleAction(requests[currentIndex].id, 'rejected')}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="text-red-500 hover:text-red-600 transition-colors"
                                    >
                                        <XCircleIcon className="h-6 w-6" />
                                    </motion.button>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{requests[currentIndex].reason}</p>
                            <p className="text-xs font-medium text-gray-800 mt-1">{requests[currentIndex].duration}</p>
                        </div>
                    </motion.div>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="text-center text-gray-500 text-sm"
                        >
                            <CheckCircleIcon className="h-8 w-8 text-green-400 mx-auto mb-2" />
                            <p>No new leave requests to review.</p>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const SlidingTicker = ({ data, title, icon: Icon, colorClass }) => {
    const [tickerItems, setTickerItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const intervalRef = useRef(null);

    const startInterval = () => {
        intervalRef.current = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % data.length);
        }, 3000);
    };

    const stopInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    useEffect(() => {
        setTickerItems(data);
        if (!isHovered) {
            startInterval();
        } else {
            stopInterval();
        }
        return () => stopInterval();
    }, [data, isHovered]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="w-full bg-white p-4 rounded-xl shadow-md overflow-hidden"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <h3 className="text-md font-bold text-gray-900 mb-3">{title}</h3>
            <div className="relative h-16 flex items-center">
                <AnimatePresence mode="wait">
                    {tickerItems.length > 0 && (
                        <motion.div
                            key={tickerItems[currentIndex]?.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20, position: 'absolute' }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg w-full"
                        >
                            <div className={`p-2 rounded-full ${colorClass.bg} ${colorClass.text}`}>
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-sm">{tickerItems[currentIndex]?.name || tickerItems[currentIndex]?.title}</p>
                                <p className="text-xs text-gray-500">{tickerItems[currentIndex]?.event || tickerItems[currentIndex]?.date}</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

const EmployerDashboard = () => {
    const [viewAllHires, setViewAllHires] = useState(false);
    const recentHiresToShow = viewAllHires ? mockDashboardData.recentHires : mockDashboardData.recentHires.slice(0, 3);
    const [showGreeting, setShowGreeting] = useState(true);
    const [greeting, setGreeting] = useState('');

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) {
            setGreeting('Good morning');
        } else if (hour >= 12 && hour < 18) {
            setGreeting('Good afternoon');
        } else {
            setGreeting('Good evening');
        }
        const timer = setTimeout(() => setShowGreeting(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 p-4 sm:p-8 text-gray-800 relative">
            {showGreeting && (
                <AnimatePresence>
                    <motion.div
                        className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] text-white text-md font-medium px-4 py-2 rounded-lg shadow-lg bg-black/60 backdrop-blur-sm"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20, transition: { duration: 1, ease: "easeOut" } }}
                    >
                        {greeting}, {mockDashboardData.userName}!
                    </motion.div>
                </AnimatePresence>
            )}

            <div className="max-w-6xl mx-auto relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-6 pt-6 sm:pt-8"
                >
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight sm:text-3xl">
                        <span className="text-base font-medium block text-gray-500">Welcome to your Employer Dashboard</span>
                    </h1>
                </motion.div>

                {/* Dashboard Overview Cards */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
                >
                    <DashboardCard
                        title="Total Employees"
                        value={mockDashboardData.totalEmployees.toLocaleString()}
                        icon={<UserGroupIcon />}
                        bgColor="bg-blue-100"
                        textColor="text-blue-600"
                    />
                    <DashboardCard
                        title="Open Positions"
                        value={mockDashboardData.openPositions}
                        icon={<BriefcaseIcon />}
                        bgColor="bg-green-100"
                        textColor="text-green-600"
                    />
                    <DashboardCard
                        title="Avg. Attrition Rate"
                        value={`${mockDashboardData.attritionRate.toFixed(2)}%`}
                        icon={<ChartBarIcon />}
                        bgColor="bg-red-100"
                        textColor="text-red-600"
                        trend="up"
                    />
                    <DashboardCard
                        title="Cost Per Hire"
                        value={`₹${(mockDashboardData.costPerHire * 82.5).toLocaleString()}`}
                        icon={<CurrencyDollarIcon />}
                        bgColor="bg-purple-100"
                        textColor="text-purple-600"
                    />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Charts Section */}
                    <motion.div
                        variants={cardVariants}
                        className="lg:col-span-2 bg-white p-4 rounded-xl shadow-md flex flex-col"
                    >
                        <h3 className="text-md font-bold text-gray-900 mb-3">Recruitment & Headcount</h3>
                        <div className="w-full h-48 sm:h-64">
                            <Bar options={barChartOptions} data={barChartData} />
                        </div>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="lg:col-span-1 bg-white p-4 rounded-xl shadow-md flex flex-col"
                    >
                        <h3 className="text-md font-bold text-gray-900 mb-3">Team Breakdown</h3>
                        <div className="w-full flex-1 flex items-center justify-center h-48 sm:h-64">
                            <Pie options={pieChartOptions} data={pieChartData} />
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="max-w-6xl mx-auto mt-6 bg-white p-4 rounded-xl shadow-md"
                >
                    <h3 className="text-md font-bold text-gray-900 mb-3">Quick Links</h3>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                        <QuickLinkCard title="Payroll Overview" icon={DocumentTextIcon} path="/employerdashboard/payroll-overview" />
                        <QuickLinkCard title="Recruitment" icon={RocketLaunchIcon} path="/employerdashboard/recruitment-pipeline" />
                        <QuickLinkCard title="Performance Reports" icon={TrophyIcon} path="/employerdashboard/performance-reports" />
                        <QuickLinkCard title="Leave Management" icon={CalendarDaysIcon} path="/employerdashboard/leave-management" />
                        <QuickLinkCard title="Onboarding & Offboarding" icon={ClipboardDocumentListIcon} path="/employerdashboard/onboarding-offboarding" />
                        <QuickLinkCard title="Announcements" icon={MegaphoneIcon} path="/employerdashboard/announcements-notifications" />
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                    <LeaveRequests />
                    <div className="flex flex-col space-y-4">
                        <SlidingTicker
                            title="Upcoming Milestones 🎉"
                            data={mockDashboardData.employeeMilestones}
                            icon={CakeIcon}
                            colorClass={{ bg: 'bg-yellow-100', text: 'text-yellow-500' }}
                        />
                        <SlidingTicker
                            title="Announcements 📢"
                            data={mockDashboardData.announcements}
                            icon={MegaphoneIcon}
                            colorClass={{ bg: 'bg-cyan-100', text: 'text-cyan-600' }}
                        />
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="bg-white p-4 rounded-xl shadow-md mt-6"
                >
                    <div className="flex justify-between items-center mb-0">
                        <h3 className="text-md font-bold text-gray-900">Recent Hires (Full List)</h3>
                        {mockDashboardData.recentHires.length > 3 && (
                            <button
                                onClick={() => setViewAllHires(!viewAllHires)}
                                className="text-sm font-semibold text-indigo-600 hover:underline transition-all"
                            >
                                {viewAllHires ? 'View Less' : 'View All'}
                            </button>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                </tr>
                            </thead>
                            <AnimatePresence mode="wait">
                                <motion.tbody className="bg-white divide-y divide-gray-200">
                                    {recentHiresToShow.map((hire) => (
                                        <motion.tr
                                            key={hire.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 10, transition: { duration: 0.2 } }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{hire.name}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{hire.role}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{hire.dept}</td>
                                        </motion.tr>
                                    ))}
                                </motion.tbody>
                            </AnimatePresence>
                        </table>
                    </div>
                </motion.div>
            </div>
            <AIChatbot />
        </div>
    );
};

export default EmployerDashboard;